import * as React from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import Avatar from 'antd/lib/avatar';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { CaretDownOutlined, CaretUpOutlined, SettingOutlined } from '@ant-design/icons';
import { RootState } from '../../../reducers/reducers';
import { selectAppGroup, selectDestinationInfoByDestinationStepConfigId } from '../../../selectors/selectors';
import { selectAppsInGroup } from '../../../selectors/memoizedSelectors';
import { selectShouldRenderWorkflowsOverviewForApp } from '../../../selectors/summaryViewSelectors';
import { AppPlatform, AppStoreType } from '../../../types';
import {
    displayTextForAppPlatform,
    checkAllAppNamesAreSame,
    isLegacyAppDisplay,
} from '../../../helpers/displayHelpers';
import uniq from 'lodash/uniq';
import { PlatformIconBox } from '../shared/components';
import { antGrey, navFlexItemStyle } from '../../styles/globalStyles';
import { HoverableNavItem } from '../shared/HoverableNavItem';
import { useSidebarColors } from '@/components/styles/themeMode';
import CurvedArrow from '../../../../assets/curved-arrow.svg';

export interface AppGroupRowProps {
    orgId: string;
    appGroupId: string;
    isSelected: boolean;
    currentAppId: string | undefined;
    allAppNamesAreSame?: boolean;
    onSelect: () => void;
    onPlatformSelect: (appEntityId: string) => void;
    onSettingsClick: (appIdParam: string) => void;
}

export const AppGroupRow: React.FC<AppGroupRowProps> = ({
    orgId,
    appGroupId,
    isSelected,
    currentAppId,
    allAppNamesAreSame: passedAllAppNamesAreSame,
    onSelect,
    onPlatformSelect,
    onSettingsClick,
}) => {
    const sc = useSidebarColors();
    const appGroup = useSelector((state: RootState) => selectAppGroup(state, appGroupId));
    const appsInGroup = useSelector((state: RootState) => selectAppsInGroup(state, appGroupId));

    const allAppNamesAreSame = React.useMemo(() => {
        if (passedAllAppNamesAreSame !== undefined) return passedAllAppNamesAreSame;
        return checkAllAppNamesAreSame(appsInGroup);
    }, [appsInGroup, passedAllAppNamesAreSame]);

    const isWorkflowsMigratedByAppId = useSelector(
        (state: RootState) =>
            appsInGroup.reduce(
                (acc, app) => {
                    acc[app.id] = selectShouldRenderWorkflowsOverviewForApp(state, orgId, app.id);
                    return acc;
                },
                {} as Record<string, boolean>,
            ),
        shallowEqual,
    );

    const finalIsLegacyByAppId = React.useMemo(() => {
        return appsInGroup.reduce(
            (acc, app) => {
                const isWorkflowsMigrated = isWorkflowsMigratedByAppId[app.id] ?? false;
                acc[app.id] = isLegacyAppDisplay(isWorkflowsMigrated, allAppNamesAreSame);
                return acc;
            },
            {} as Record<string, boolean>,
        );
    }, [appsInGroup, isWorkflowsMigratedByAppId, allAppNamesAreSame]);
    const firstApp = appsInGroup[0];

    const hasMultiplePlatforms = appsInGroup.length > 1;
    const isCollapsible = appsInGroup.length > 1;
    const [isExpanded, setIsExpanded] = React.useState(appsInGroup.length <= 3);

    const singleAppDestinations = useSelector(
        (state: RootState) =>
            !hasMultiplePlatforms && firstApp ?
                Object.values(selectDestinationInfoByDestinationStepConfigId(state, firstApp.id))
            :   [],
        shallowEqual,
    );

    const singleAppPlatformText = React.useMemo(() => {
        if (hasMultiplePlatforms || !firstApp) return null;
        const isWorkflowsMigrated = isWorkflowsMigratedByAppId[firstApp.id] ?? false;
        if (!isWorkflowsMigrated || singleAppDestinations.length === 0) return null;
        const uniquePlatforms = uniq(singleAppDestinations.map((d) => d.platform));
        if (uniquePlatforms.length <= 1) return null;
        return uniquePlatforms.map((p) => displayTextForAppPlatform(p)).join(' / ');
    }, [hasMultiplePlatforms, firstApp, isWorkflowsMigratedByAppId, singleAppDestinations]);

    if (!appGroup || !firstApp) return null;

    const imageUrl = appGroup.appGroupImageUrl || firstApp.appSettings.appImageUrl;
    const displayName = appGroup.name || firstApp.appSettings.appName;

    const handleSettingsClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSettingsClick(firstApp.appId);
    };

    const handleRowClick = () => {
        if (isCollapsible) {
            setIsExpanded(!isExpanded);
        } else if (!hasMultiplePlatforms) {
            onSelect();
        }
    };

    return (
        <>
            <HoverableNavItem
                isSelected={isSelected && !hasMultiplePlatforms}
                onClick={handleRowClick}
                style={!isCollapsible && hasMultiplePlatforms ? { cursor: 'default' } : undefined}
            >
                <Flex align="center" style={{ ...navFlexItemStyle, gap: 12 }}>
                    <Avatar
                        shape="square"
                        size={32}
                        src={imageUrl}
                        style={{ borderRadius: 6, border: 'none', flexShrink: 0 }}
                    >
                        {displayName?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Flex align="center" style={navFlexItemStyle}>
                        <Text
                            style={{
                                color: sc.text,
                                fontWeight: isSelected && !hasMultiplePlatforms ? 600 : 400,
                                flex: 1,
                                minWidth: 0,
                            }}
                            ellipsis
                        >
                            {displayName}
                            {singleAppPlatformText && (
                                <span style={{ color: sc.platformMuted, marginLeft: 4, fontWeight: 400 }}>
                                    | {singleAppPlatformText}
                                </span>
                            )}
                        </Text>
                    </Flex>
                </Flex>
                {isCollapsible ?
                    isExpanded ?
                        <CaretDownOutlined style={{ color: antGrey.grey6, fontSize: 16 }} />
                    :   <CaretUpOutlined style={{ color: antGrey.grey6, fontSize: 16 }} />
                : !hasMultiplePlatforms ?
                    <Tooltip title="Settings" mouseEnterDelay={0.4}>
                        <SettingOutlined
                            style={{ color: sc.icon, fontSize: 16, cursor: 'pointer' }}
                            onClick={handleSettingsClick}
                        />
                    </Tooltip>
                :   null}
            </HoverableNavItem>

            {hasMultiplePlatforms && isExpanded && (
                <div>
                    {appsInGroup.map((appInGroup) => (
                        <PlatformRow
                            key={appInGroup.id}
                            appInGroup={appInGroup}
                            isLegacyApp={finalIsLegacyByAppId[appInGroup.id]}
                            isSelected={appInGroup.appId === currentAppId}
                            onSelect={() => onPlatformSelect(appInGroup.id)}
                            onSettingsClick={() => onSettingsClick(appInGroup.appId)}
                        />
                    ))}
                </div>
            )}
        </>
    );
};

interface PlatformRowProps {
    appInGroup: {
        id: string;
        appId: string;
        appSettings: {
            platform: AppPlatform;
            appStoreType?: AppStoreType;
            appImageUrl?: string;
            appName?: string;
        };
        isRestrictedApp?: boolean;
    };
    isLegacyApp: boolean;
    isSelected: boolean;
    onSelect: () => void;
    onSettingsClick: () => void;
}

const PlatformRow: React.FC<PlatformRowProps> = ({
    appInGroup,
    isLegacyApp,
    isSelected,
    onSelect,
    onSettingsClick,
}) => {
    const isDisabled = appInGroup.isRestrictedApp;
    const platformText = displayTextForAppPlatform(
        appInGroup.appSettings.platform,
        appInGroup.appSettings.appStoreType,
    );

    const handleSettingsClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSettingsClick();
    };

    const sc = useSidebarColors();
    return (
        <HoverableNavItem
            onClick={onSelect}
            isSelected={isSelected}
            isDisabled={isDisabled}
            style={{ paddingLeft: 32 }}
        >
            <Flex align="center" gap={12} style={navFlexItemStyle}>
                <img src={CurvedArrow} width={16} height={13} style={{ flexShrink: 0 }} />
                {isLegacyApp ?
                    <>
                        <Avatar
                            shape="square"
                            size={24}
                            src={appInGroup.appSettings.appImageUrl}
                            style={{ borderRadius: 4, border: 'none', flexShrink: 0 }}
                        >
                            {appInGroup.appSettings.appName?.charAt(0).toUpperCase()}
                        </Avatar>
                        <Text
                            style={{
                                color: isDisabled ? sc.textMuted : sc.text,
                                fontWeight: isSelected ? 600 : 400,
                                flex: 1,
                                minWidth: 0,
                            }}
                            ellipsis
                        >
                            {appInGroup.appSettings.appName}
                            <Text style={{ color: sc.platformMuted, fontWeight: 400, marginLeft: 4 }}>
                                | {platformText}
                            </Text>
                        </Text>
                    </>
                :   <>
                        <PlatformIconBox platform={appInGroup.appSettings.platform} />
                        <Text
                            style={{
                                color: isDisabled ? sc.textMuted : sc.text,
                                fontWeight: isSelected ? 600 : 400,
                                flex: 1,
                                minWidth: 0,
                            }}
                            ellipsis
                        >
                            {platformText}
                        </Text>
                    </>
                }
            </Flex>
            <Tooltip title="Settings" mouseEnterDelay={0.4}>
                <SettingOutlined style={{ color: sc.icon, fontSize: 16 }} onClick={handleSettingsClick} />
            </Tooltip>
        </HoverableNavItem>
    );
};
